import { useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';

/**
 * Tracks which method a returning user last authenticated with so we can
 * surface a subtle "Last used" hint on the login screen. We can't know the
 * user's identity before they sign in, so this lives entirely in localStorage
 * and is per-device/browser by design.
 */
export type LoginMethod = 'google' | 'facebook' | 'apple' | 'email';

const STORAGE_KEY = 'lastUsedLoginMethod';

const isLoginMethod = (value: unknown): value is LoginMethod =>
  value === 'google' ||
  value === 'facebook' ||
  value === 'apple' ||
  value === 'email';

interface UseLastUsedLoginMethodReturn {
  lastUsedLoginMethod: LoginMethod | undefined;
  setLastUsedLoginMethod: (method: LoginMethod) => void;
}

export function useLastUsedLoginMethod(): UseLastUsedLoginMethodReturn {
  const [storedValue, setStoredValue] = useLocalStorage<LoginMethod>(
    STORAGE_KEY,
  );

  // localStorage is unavailable during SSR/first paint. Defer reading it until
  // after mount so the server and initial client render agree (no hydration
  // mismatch) and the badge simply fades in once we know the value.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  return {
    lastUsedLoginMethod:
      mounted && isLoginMethod(storedValue) ? storedValue : undefined,
    setLastUsedLoginMethod: setStoredValue,
  };
}
